import { datadogLogs } from '@datadog/browser-logs';
import { useStatsigClient } from '@statsig/react-bindings';
import { createClient, Entry, EntrySkeletonType } from 'contentful';
import { useEffect, useState } from 'react';

import { useImpState } from '../../app/hooks';
import { Color, setColor } from '../../app/merchantDataSlice';
import { AppDispatch } from '../../app/store';
import Gates from '../../statsig/gates';
import { isDev, isStaging } from '../../utils/environments';
import { PRODUCT_UUID } from '../../utils/productUUIDConstants';
import { getColor } from '../api';
import { getMockSteps, OnboardingStep } from './onboardingSteps';

const CONTENTFUL_SPACE_ID = 'sb2r1vk07tgu';
const CONTENTFUL_ACCESS_TOKEN = 'ZQkUJ96iyNn9q3WUEmW6NisYGj_DzYh_Z5u9AiqBiBM';

export const contentfulClient = createClient({
  space: CONTENTFUL_SPACE_ID,
  accessToken: CONTENTFUL_ACCESS_TOKEN,
  environment: isDev() || isStaging() ? 'stg' : 'master',
});

enum ContentTypes {
  POST_APPROVAL_STEPS = 'postApprovalSteps',
  PARTNER_BRANDING = 'partnerBranding',
  // Add other content types as needed
}
type ReferenceEntry = Entry<EntrySkeletonType, undefined, string>;

export const useContentfulOnboardingSteps = () => {
  const { merchantData, language } = useImpState();

  const isBooking = merchantData.productUUID === PRODUCT_UUID.BOOKING;

  const [steps, setSteps] = useState<OnboardingStep[]>(getMockSteps(isBooking));
  const [loading, setLoading] = useState(true);
  const { checkGate } = useStatsigClient();

  useEffect(() => {
    if (!checkGate(Gates.Contentful)) {
      setLoading(false);
      return;
    }

    contentfulClient
      .getEntries({
        content_type: ContentTypes.POST_APPROVAL_STEPS,
        'fields.productUUIDs[in]': merchantData.productUUID,
        locale: language.locale,
      })
      .then((response) => {
        setLoading(false);
        const entry = response.items[0];
        if (!entry) throw new Error('Missing entry');

        const onboardingSteps = convertEntryToOnboardingSteps(entry);
        if (onboardingSteps.length > 0) {
          setSteps(onboardingSteps);
        } else {
          throw new Error('Failed to parse steps');
        }
      })
      .catch((err) => {
        datadogLogs.logger.error(
          `Error fetching postApprovalSteps - ${merchantData.productUUID}`,
          err
        );
        setLoading(false);
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { steps, loading };
};

const convertEntryToOnboardingSteps = (
  entry: ReferenceEntry
): OnboardingStep[] => {
  const rawSteps = entry.fields.steps;

  if (!Array.isArray(rawSteps)) {
    return [];
  }

  return rawSteps.map((step: any) => {
    const key = step.sys?.contentType.sys?.id;
    const content = step.fields;

    return { key, content };
  });
};

export const fetchPartnerBrandingFromContentful =
  (productUUID: string) => async (dispatch: AppDispatch) => {
    try {
      const response = await contentfulClient.getEntries({
        content_type: ContentTypes.PARTNER_BRANDING,
        'fields.productUUID': productUUID,
      });
      const entryFields = response.items[0]?.fields;
      if (!entryFields) throw new Error('Missing entry');

      const color = parseColorFromEntry(entryFields.colors as ReferenceEntry);
      if (!color) throw new Error('Failed to parse colors');

      dispatch(setColor(color));
    } catch (err) {
      datadogLogs.logger.error(
        `Error fetching partnerBranding - ${productUUID}`,
        { err }
      );
      dispatch(getColor(productUUID)); // Fallback to legacy
    }
  };

const parseColorFromEntry = (colorEntry: ReferenceEntry): Color | null => {
  const { dark, light, contrastDark, contrastLight } = colorEntry.fields;

  if (typeof dark !== 'string' || typeof light !== 'string') {
    return null;
  }

  return {
    dark,
    light,
    ...(typeof contrastDark === 'string' && { contrastDark }),
    ...(typeof contrastLight === 'string' && { contrastLight }),
  };
};
